import React, {useRef} from "react";
import {motion} from "framer-motion";
import Typography from "./Typography";
import {getColor} from "../styles/color";
import {DEFAULT_TRANSITION} from "../../rise/constants/transition";

export interface Stat {
    label: string;
    value: number;
    labels: Array<string>;
}

interface StatEntryProps {
    stat: Stat;
    index?: number;
}

export default function StatEntry({stat, index}: StatEntryProps) {
    const container = useRef<HTMLDivElement>(null);
    const isOnEdge = stat.value < 0.1 || stat.value > 0.9;
    const width = stat.value;
    const rest = 1 - stat.value;

    let barWidth = width - 0.01;
    let restWidth = rest - 0.02;

    if (stat.value < 0.1) {
        barWidth = 0;
        restWidth = 1;
    } else if (stat.value > 0.9) {
        barWidth = 1;
        restWidth = 0;
    }

    const fullWidth = container.current?.clientWidth || 0;

    return (
        <div
            ref={container}
            style={{
                marginBottom: 11,
                borderLeft: '2px solid black',
                borderRight: '2px solid black',
            }}
        >
            <div
                style={{
                    paddingLeft: 5,
                    paddingRight: 5,
                    paddingBottom: 5,
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                }}
            >
                {stat.labels.map((label, index) => (
                    <Typography
                        key={index}
                        style={{marginTop: -2}}
                        size={12}
                        variant={"jordan"}
                        color={'near-black'}
                    >
                        {label}
                    </Typography>
                ))}

                <div
                    style={{
                        position: 'absolute',
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        size={12}
                        variant={"jordan"}
                        style={{
                            marginTop: -4,
                            textTransform: 'capitalize'
                        }}
                        color={'near-black'}
                    >
                        {stat.label}
                    </Typography>
                </div>
            </div>

            <div
                style={{
                    height: 12,
                    paddingLeft: 5,
                    paddingRight: 5,
                    width: '100%',
                }}
            >
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                    }}
                >
                    <motion.div
                        style={{
                            borderRadius: 2,
                            height: 12,
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            background: getColor('near-black'),
                        }}
                        animate={{
                            width: `${barWidth * 100}%`,
                        }}
                        transition={{
                            ...DEFAULT_TRANSITION,
                            duration: 0.5,
                            delay: (index * .051) + 1,
                        }}
                    />

                    <motion.div
                        // className={`StatComponentRepeatingLines`}
                        style={{
                            borderRadius: 2,
                            height: 12,
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            overflow: 'hidden',
                        }}
                        animate={{
                            width: `${restWidth * 100}%`,
                        }}
                        transition={{
                            ...DEFAULT_TRANSITION,
                            duration: 0.5,
                            delay: (index * .051) + 1.05,
                        }}
                    >
                        <img
                            style={{
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'right',
                            }}
                            src={`data:image/svg+xml,<svg width="373" height="24" viewBox="0 0 373 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(%23clip0_109_373)"><line x1="159.542" y1="-6.69593" x2="77.597" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="343.014" y1="-6.69593" x2="261.07" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="67.8063" y1="-6.69593" x2="-14.1383" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="251.278" y1="-6.69593" x2="169.333" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="434.751" y1="-6.69593" x2="352.806" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="21.9382" y1="-6.69593" x2="-60.0065" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="205.411" y1="-6.69593" x2="123.466" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="388.882" y1="-6.69593" x2="306.937" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="113.674" y1="-6.69593" x2="31.7298" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="297.146" y1="-6.69593" x2="215.201" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="182.476" y1="-6.69593" x2="100.532" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="365.948" y1="-6.69593" x2="284.003" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="90.7399" y1="-6.69593" x2="8.79524" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="274.212" y1="-6.69593" x2="192.267" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="446.217" y1="-6.69593" x2="364.273" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="44.8718" y1="-6.69593" x2="-37.0729" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="228.343" y1="-6.69593" x2="146.399" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="411.816" y1="-6.69593" x2="329.871" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="136.608" y1="-6.69593" x2="54.6634" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="320.08" y1="-6.69593" x2="238.135" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="102.207" y1="-6.69593" x2="20.262" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="285.679" y1="-6.69593" x2="203.735" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="10.4704" y1="-6.69593" x2="-71.4743" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="193.943" y1="-6.69593" x2="111.998" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="377.415" y1="-6.69593" x2="295.47" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="148.075" y1="-6.69593" x2="66.1302" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="331.548" y1="-6.69593" x2="249.603" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="56.3395" y1="-6.69593" x2="-25.6051" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="239.81" y1="-6.69593" x2="157.866" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="423.283" y1="-6.69593" x2="341.338" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="125.14" y1="-6.69593" x2="43.1956" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="308.613" y1="-6.69593" x2="226.668" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="33.404" y1="-6.69593" x2="-48.5407" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="216.878" y1="-6.69593" x2="134.933" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="400.35" y1="-6.69593" x2="318.406" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="171.009" y1="-6.69593" x2="89.0648" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="354.482" y1="-6.69593" x2="272.537" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="79.2722" y1="-6.69593" x2="-2.67253" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/><line x1="262.746" y1="-6.69593" x2="180.801" y2="26.7509" stroke="%23111111" strokeWidth="1.79179"/></g><defs><clipPath id="clip0_109_373"><rect width="373" height="24" fill="white"/></clipPath></defs></svg>`}
                        />
                    </motion.div>

                    <motion.div
                        style={{
                            // to exactly match other border bars
                            width: "2px",
                            height: 16,
                            top: -4,
                            position: 'absolute',
                            background: getColor('near-black')
                        }}
                        animate={{
                            left: `${(width - 0.002) * 100}%`,
                            opacity: isOnEdge ? 0 : 1,
                        }}
                        transition={{
                            ...DEFAULT_TRANSITION,
                            duration: 0.5,
                            delay: (index * .051) + 1.05,
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
